import { Localization } from "@viamap/viamap2-common";
import { useMemo, useState, type MouseEvent, type ReactElement, type ReactNode } from "react";
import { GlassButton } from "src/components/GlassButtons";
import {
  defaultTingbogKey,
  listPropertyTingbogs,
} from "src/dataCollector/parseTinglysningsDataXml";
import { FeatureType } from "src/states/ApplicationState";
import { GlassFoldUdBox, KeyValueLine } from "./PropertyComponents";
import { PropertyData } from "./PropertyInfoCollectorTypes";
import { PropInfoGroupFormatters, TingbogAttestCadastralHints } from "./PropertyInfoGroupFormatters";
import { TingbogServitutterSectionTitle } from "./TingbogServitutBatch";

type AccessFn = (feature: FeatureType) => boolean;

type Props = {
  data: Partial<PropertyData>;
  hasAccessToFeature: AccessFn;
  foldetUd: boolean;
  cadastralHints?: TingbogAttestCadastralHints;
  onCopyServitutterToExcel?: (event: MouseEvent<HTMLElement>, displayData: Partial<PropertyData>) => void;
  extraServitutActions?: (displayData: Partial<PropertyData>) => ReactNode;
};

function titled(base: string, label: string, hasMany: boolean): string {
  return hasMany ? `${base} (${label})` : base;
}

function ownerText(tingbog: {
  SenesteAdkomst?: {
    adkomsthaver?: { displayText?: string }[];
  };
}): string {
  return tingbog.SenesteAdkomst?.adkomsthaver
    ?.find((haver) => haver.displayText && !haver.displayText.startsWith("Bemærk"))
    ?.displayText || "";
}

export function useTingbogSectionElements(props: Props) {
  const { data, hasAccessToFeature, foldetUd } = props;
  const entries = useMemo(
    () => listPropertyTingbogs(data.ViamapTingBogAll, data.ViamapTingBog),
    [data.ViamapTingBog, data.ViamapTingBogAll],
  );
  const fallbackKey = defaultTingbogKey(entries, data.ViamapTingBog);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const resolvedKey = (selectedKey && entries.some((entry) => entry.key === selectedKey))
    ? selectedKey
    : fallbackKey;
  const selected = entries.find((entry) => entry.key === resolvedKey) ?? entries[0];
  const hasMany = entries.length > 1;
  const allRequested = "ViamapTingBogAll" in data;
  const allLoading = Boolean(data.ViamapTingBog) && !allRequested;
  const displayData = selected
    ? { ...data, ViamapTingBog: selected.tingbog } as PropertyData
    : data as PropertyData;
  const label = selected ? selected.label : "";

  if (!selected && !allLoading) {
    return [];
  }

  const sections: ReactElement[] = [];

  if (hasMany || allLoading) {
    sections.push(
      <GlassFoldUdBox
        key="Tingbog - Vælger"
        group="Tingbog"
        title={Localization.getText("Land registers")}
        boxKey="Tingbog - Andre tingbøger"
        foldetUd={true}
      >
        {allLoading ? (
          <KeyValueLine
            keepEmpty
            valKey={Localization.getText("Fetching other land registers…")}
            value=""
          />
        ) : entries.map((entry) => {
          const isSelected = entry.key === selected?.key;
          const owner = ownerText(entry.tingbog);
          const fordeling = entry.tingbog.stamOplysninger?.fordelingstal;
          const fordelingText = fordeling?.taeller && fordeling?.naevner
            ? `${fordeling.taeller}/${fordeling.naevner}`
            : "";
          const detail = [owner, fordelingText].filter(Boolean).join(" · ");
          return (
            <KeyValueLine
              key={entry.key}
              keepEmpty
              valKey={entry.label}
              value={
                <div style={{ display: "flex", alignItems: "center", gap: "0.5em", justifyContent: "flex-end", width: "100%" }}>
                  {detail ? <span>{detail}</span> : null}
                  {isSelected ? (
                    <span style={{ fontWeight: 600 }}>{Localization.getText("Showing")}</span>
                  ) : (
                    <GlassButton
                      className="tingbog-download-button"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setSelectedKey(entry.key);
                      }}
                    >
                      {Localization.getText("Show this land register")}
                    </GlassButton>
                  )}
                </div>
              }
            />
          );
        })}
      </GlassFoldUdBox>,
    );
  }

  if (!selected) {
    return sections;
  }

  sections.push(
    <GlassFoldUdBox
      key="Tingbog - Adkomster"
      group="Tingbog"
      title={titled("Tingbog - Adkomster", label, hasMany)}
      boxKey="Tingbog - Adkomster"
      foldetUd={foldetUd}
    >
      {PropInfoGroupFormatters.TingbogAdkomster(displayData, hasAccessToFeature)}
    </GlassFoldUdBox>,
    <GlassFoldUdBox
      key="Tingbog - Hæftelser"
      group="Tingbog"
      title={titled("Tingbog - Hæftelser", label, hasMany)}
      boxKey="Tingbog - Hæftelser"
      foldetUd={foldetUd}
    >
      {PropInfoGroupFormatters.TingbogHæftelser(displayData, hasAccessToFeature)}
    </GlassFoldUdBox>,
    <GlassFoldUdBox
      key="Tingbog - Servitutter"
      group="Tingbog"
      title={
        <TingbogServitutterSectionTitle
          sectionTitle={titled("Tingbog - Servitutter", label, hasMany)}
          hasAccessToFeature={hasAccessToFeature}
          onCopyToExcel={props.onCopyServitutterToExcel
            ? (event) => props.onCopyServitutterToExcel?.(event, displayData)
            : undefined}
          extraActions={props.extraServitutActions?.(displayData)}
        />
      }
      boxKey="Tingbog - Servitutter"
      foldetUd={foldetUd}
    >
      {PropInfoGroupFormatters.TingbogServitutter(displayData, hasAccessToFeature)}
    </GlassFoldUdBox>,
    <GlassFoldUdBox
      key="Tingbog - Øvrigt"
      group="Tingbog"
      title={titled("Tingbog - Øvrigt", label, hasMany)}
      boxKey="Tingbog - Øvrigt"
      foldetUd={foldetUd}
    >
      {PropInfoGroupFormatters.TingbogEjendom(displayData, hasAccessToFeature, props.cadastralHints)}
    </GlassFoldUdBox>,
  );

  return sections;
}
